import { Stack } from "@mantine/core"
import { Card, Loader, SectionHeader } from "@tfg/ui"
import { isEqual } from "lodash"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { FindIngestionSettingsResponseDto } from "~/gen/api"
import { useStandardsQuery } from "~/pages/Admin/MasterLists/CertificatesTypes/Standards/hooks"
import { DefaultStandards } from "~/pages/Brand/OrganizationSettings/IngestionSettings/components/DefaultStandards"
import { SettingsRow } from "~/pages/Brand/OrganizationSettings/IngestionSettings/components/SettingsRow"
import { TraceabilityMethods } from "~/pages/Brand/OrganizationSettings/IngestionSettings/components/TraceabilityMethods"
import { useUpdateIngestionSettingsMutation } from "~/pages/Brand/OrganizationSettings/IngestionSettings/hooks/useUpdateIngestionSettings"
import { useAuth } from "~/store/auth-context"

type Props = {
  data: FindIngestionSettingsResponseDto
}

export function IngestionClientSettings({ data }: Props) {
  const { t } = useTranslation("organizationSettings")
  const { user } = useAuth()
  const clientId = user?.organization?.id

  const [ingestionSettings, setIngestionSettings] =
    useState<FindIngestionSettingsResponseDto>({
      traceabilityMethod: data.traceabilityMethod,
      defaultStandards: data.defaultStandards,
    })

  const { mutate: updateSettings, isLoading: isUpdating } =
    useUpdateIngestionSettingsMutation()

  const { data: standards } = useStandardsQuery()

  useEffect(() => {
    if (isEqual(data, ingestionSettings)) return

    if (!clientId) return

    updateSettings({
      traceabilityMethod: ingestionSettings.traceabilityMethod,
      defaultStandards: ingestionSettings.defaultStandards,
      clientId,
    })
  }, [data, ingestionSettings, updateSettings, clientId])

  return (
    <>
      <SectionHeader
        title={t("ingestionSettings.title")}
        subtitle={t("ingestionSettings.description")}
      />
      <Card>
        <Card.Section>
          {isUpdating ? (
            <Loader fullPage />
          ) : (
            <Stack m={0} gap={0}>
              <SettingsRow
                title={t("ingestionSettings.traceabilityMethod.title")}
                description={t(
                  "ingestionSettings.traceabilityMethod.description"
                )}
              >
                <TraceabilityMethods
                  update={setIngestionSettings}
                  traceabilityMethod={ingestionSettings.traceabilityMethod}
                />
              </SettingsRow>
              <SettingsRow
                title={t("ingestionSettings.defaultStandards.title")}
                description={t(
                  "ingestionSettings.defaultStandards.description"
                )}
              >
                <DefaultStandards
                  update={setIngestionSettings}
                  defaultStandards={ingestionSettings.defaultStandards}
                  standardsOptions={
                    standards?.map((standard) => standard.name) ?? []
                  }
                />
              </SettingsRow>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </>
  )
}
